import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  Divider,
  Grid,
  Chip,
  CircularProgress,
  TextField,
  MenuItem,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Save as SaveIcon,
  CloudDownload as CloudDownloadIcon,
} from '@mui/icons-material';
import { userManagementApi } from '../shared/api/userApi';
import backendApi from '../shared/api/backendApi';

function Settings({ user }) {
  const [settings, setSettings] = useState({
    // Notification settings
    emailNotifications: true,
    pushNotifications: false,
    invoiceReminders: true,
    paymentReminders: true,

    // Appearance settings
    theme: 'light',
    language: 'en',
    currency: 'INR',
    dateFormat: 'DD/MM/YYYY',

    // Privacy settings
    profileVisibility: 'private',
    dataSharing: false,

    // Business settings
    autoBackup: false,

    // Invoice reminder automation
    reminderEnabled: false,
    reminderSendMode: 'text',
    reminderDefaultDueDays: 30,
    reminderDaysBeforeDue: '3,1',
    reminderOverdueDays: '1,3,7',
    reminderTime: '09:00',
    reminderCustomFooter: '',
    reminderSenderOverride: '',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffActionLoadingId, setStaffActionLoadingId] = useState(null);
  const [pendingStaffRequests, setPendingStaffRequests] = useState([]);
  const [staffMembers, setStaffMembers] = useState([]);
  const [runningReminderNow, setRunningReminderNow] = useState(false);
  const normalizedAccessRole = String(user?.accessRole || '').trim().toLowerCase();
  const hasAdminAccess = normalizedAccessRole === 'admin' || normalizedAccessRole === 'owner';

  // Load settings on component mount
  useEffect(() => {
    loadSettings();
  }, [user.id]);

  useEffect(() => {
    if (hasAdminAccess) {
      loadStaffManagementData();
    }
  }, [hasAdminAccess]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      // First try to load from backend
      try {
        const [response, reminderResponse] = await Promise.all([
          userManagementApi.getUserSettings(user.id),
          backendApi.getReminderSettings().catch(() => null),
        ]);

        if (response.success) {
          const reminderSettings = reminderResponse?.settings || {};
          const mergedSettings = {
            ...response.settings,
            autoBackup: false,
            reminderEnabled: Boolean(reminderSettings.enabled),
            reminderSendMode: reminderSettings.sendMode || 'text',
            reminderDefaultDueDays: reminderSettings.defaultDueDays || 30,
            reminderDaysBeforeDue: Array.isArray(reminderSettings.daysBeforeDue)
              ? reminderSettings.daysBeforeDue.join(',')
              : '3,1',
            reminderOverdueDays: Array.isArray(reminderSettings.overdueDays)
              ? reminderSettings.overdueDays.join(',')
              : '1,3,7',
            reminderTime: reminderSettings.reminderTime || '09:00',
            reminderCustomFooter: reminderSettings.customFooter || '',
            reminderSenderOverride: reminderSettings.whatsappSenderOverride || '',
          };
          setSettings(mergedSettings);
          // Update localStorage with server data
          localStorage.setItem(`settings_${user.id}`, JSON.stringify(mergedSettings));
          return;
        }
      } catch (apiError) {
        console.warn('Failed to load settings from backend, using localStorage:', apiError);
      }

      // Fallback to localStorage
      const localSettings = localStorage.getItem(`settings_${user.id}`);
      if (localSettings) {
        const parsedSettings = JSON.parse(localSettings);
        setSettings({
          ...parsedSettings,
          autoBackup: false,
        });
      }
    } catch (error) {
      setError('Failed to load settings');
      console.error('Settings load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Prepare settings data for saving
      const settingsData = {
        ...settings,
        autoBackup: false,
        userId: user.id,
        updatedAt: new Date().toISOString(),
        createdAt: settings.createdAt || new Date().toISOString(),
      };

      // Save to backend first
      try {
        await userManagementApi.saveUserSettings(user.id, settingsData);
        await backendApi.saveReminderSettings({
          enabled: Boolean(settings.reminderEnabled),
          sendMode: settings.reminderSendMode,
          defaultDueDays: Number(settings.reminderDefaultDueDays) || 30,
          daysBeforeDue: settings.reminderDaysBeforeDue,
          overdueDays: settings.reminderOverdueDays,
          reminderTime: settings.reminderTime || '09:00',
          customFooter: settings.reminderCustomFooter,
          whatsappSenderOverride: settings.reminderSenderOverride,
        });
        console.log('✅ Settings saved to backend');
      } catch (apiError) {
        console.warn('⚠️ Failed to save to backend, saving locally only:', apiError);
        // Continue with localStorage save even if backend fails
      }

      // Update localStorage
      localStorage.setItem(`settings_${user.id}`, JSON.stringify(settingsData));

      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to save settings. Please try again.');
      console.error('Settings save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSyncData = async () => {
    try {
      setSyncing(true);
      setError(null);

      const result = await userManagementApi.syncLocalData(user);
      if (result.success) {
        setSuccess('Data synced with server successfully!');
        // Reload settings data
        await loadSettings();
      } else {
        setError('Failed to sync data with server');
      }

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to sync data. Please try again.');
      console.error('Data sync error:', err);
    } finally {
      setSyncing(false);
    }
  };

  const handleRunReminderNow = async () => {
    try {
      setRunningReminderNow(true);
      setError(null);
      const result = await backendApi.runReminderScanNow();
      const first = result?.results?.[0] || {};
      setSuccess(
        `Reminder scan complete. Sent: ${first.remindersSent || 0}, Skipped: ${first.remindersSkipped || 0}, Scanned: ${first.invoicesScanned || 0}`
      );
      setTimeout(() => setSuccess(null), 4000);
    } catch (runError) {
      const timeoutLike = runError?.code === 'ECONNABORTED' || String(runError?.message || '').toLowerCase().includes('timeout');
      setError(
        timeoutLike
          ? 'Reminder scan is taking longer than expected. Please wait and retry in a moment.'
          : (runError?.response?.data?.message || 'Failed to run reminder scan.')
      );
    } finally {
      setRunningReminderNow(false);
    }
  };

  const loadStaffManagementData = async () => {
    try {
      setStaffLoading(true);
      const [pendingResult, staffResult] = await Promise.all([
        backendApi.getPendingStaffRequests(),
        backendApi.getStaffMembers(),
      ]);

      setPendingStaffRequests(pendingResult.requests || []);
      setStaffMembers(staffResult.staff || []);
    } catch (staffError) {
      console.error('Failed to load staff management data:', staffError);
    } finally {
      setStaffLoading(false);
    }
  };

  const handleApproveStaffRequest = async (requestId, role = 'staff') => {
    const actionKey = `pending-${requestId}`;
    try {
      setStaffActionLoadingId(actionKey);
      await backendApi.approveStaffRequest(requestId, role);
      setSuccess(`Staff request approved as ${role}.`);
      await loadStaffManagementData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (approveError) {
      console.error('Approve request error:', approveError);
      setError(approveError?.response?.data?.message || 'Failed to approve request');
    } finally {
      setStaffActionLoadingId(null);
    }
  };

  const handleRejectStaffRequest = async (requestId) => {
    const actionKey = `pending-${requestId}`;
    try {
      setStaffActionLoadingId(actionKey);
      await backendApi.rejectStaffRequest(requestId);
      setSuccess('Staff request rejected.');
      await loadStaffManagementData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (rejectError) {
      console.error('Reject request error:', rejectError);
      setError(rejectError?.response?.data?.message || 'Failed to reject request');
    } finally {
      setStaffActionLoadingId(null);
    }
  };

  const handleUpdateStaffRole = async (staffId, role) => {
    const actionKey = `active-${staffId}`;
    try {
      setStaffActionLoadingId(actionKey);
      await backendApi.updateStaffRole(staffId, role);
      setSuccess(`Staff role updated to ${role}.`);
      await loadStaffManagementData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (updateError) {
      console.error('Update staff role error:', updateError);
      setError(updateError?.response?.data?.message || 'Failed to update staff role');
    } finally {
      setStaffActionLoadingId(null);
    }
  };

  const handleRemoveStaffAccess = async (staffId, staffName = 'this staff member') => {
    if (!window.confirm(`Remove access for ${staffName}? They will be logged out and must request access again.`)) {
      return;
    }

    const actionKey = `active-${staffId}`;
    try {
      setStaffActionLoadingId(actionKey);
      await backendApi.removeStaffAccess(staffId);
      setSuccess('Staff access removed successfully.');
      await loadStaffManagementData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (removeError) {
      console.error('Remove staff access error:', removeError);
      setError(removeError?.response?.data?.message || 'Failed to remove staff access');
    } finally {
      setStaffActionLoadingId(null);
    }
  };

  const formatDateTime = (dateValue) => {
    if (!dateValue) return 'Never';
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) return 'Never';
    return parsed.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateValue) => {
    if (!dateValue) return 'No recent activity';
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) return 'No recent activity';

    const now = Date.now();
    const diffMs = now - parsed.getTime();
    if (diffMs < 60 * 1000) return 'Just now';

    const diffMinutes = Math.floor(diffMs / (60 * 1000));
    if (diffMinutes < 60) return `${diffMinutes} min ago`;

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;

    const diffMonths = Math.floor(diffDays / 30);
    return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2
          }}
        >
          Settings & Preferences
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Manage active features and staff access controls
        </Typography>
      </Box>

      {/* Success/Error Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Card sx={{ borderRadius: 3, width: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ mr: 2, color: 'success.main', fontSize: 28 }} />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Active Settings
                </Typography>
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                Notification reminders, theme, language, date formatting, and privacy-sharing controls are temporarily hidden until fully implemented.
              </Alert>

              <FormControlLabel
                control={<Switch checked={false} disabled color="primary" />}
                label="Automatic Data Backup (Temporarily Disabled)"
              />

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Backup automation will be re-enabled after the scheduled backup system is live.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Card sx={{ borderRadius: 3, width: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5 }}>
                Invoice Reminder Automation
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Automatically send WhatsApp reminders before due date, on due date, and overdue.
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(settings.reminderEnabled)}
                    onChange={(e) => setSettings((prev) => ({ ...prev, reminderEnabled: e.target.checked }))}
                  />
                }
                label="Enable automatic invoice reminders"
              />

              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    select
                    fullWidth
                    label="Send Mode"
                    value={settings.reminderSendMode || 'text'}
                    onChange={(e) => setSettings((prev) => ({ ...prev, reminderSendMode: e.target.value }))}
                  >
                    <MenuItem value="text">Text</MenuItem>
                    <MenuItem value="pdf">PDF</MenuItem>
                    <MenuItem value="both">Text + PDF</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Default Due Days"
                    value={settings.reminderDefaultDueDays}
                    onChange={(e) => setSettings((prev) => ({ ...prev, reminderDefaultDueDays: e.target.value }))}
                    inputProps={{ min: 1, max: 365 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="time"
                    label="Reminder Time"
                    value={settings.reminderTime || '09:00'}
                    onChange={(e) => setSettings((prev) => ({ ...prev, reminderTime: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Days Before Due (comma separated)"
                    value={settings.reminderDaysBeforeDue || '3,1'}
                    onChange={(e) => setSettings((prev) => ({ ...prev, reminderDaysBeforeDue: e.target.value }))}
                    placeholder="3,1"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Overdue Reminder Days (comma separated)"
                    value={settings.reminderOverdueDays || '1,3,7'}
                    onChange={(e) => setSettings((prev) => ({ ...prev, reminderOverdueDays: e.target.value }))}
                    placeholder="1,3,7"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Custom Footer (optional)"
                    value={settings.reminderCustomFooter || ''}
                    onChange={(e) => setSettings((prev) => ({ ...prev, reminderCustomFooter: e.target.value }))}
                    placeholder="Please clear dues on time"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="WhatsApp Sender Override (optional)"
                    value={settings.reminderSenderOverride || ''}
                    onChange={(e) => setSettings((prev) => ({ ...prev, reminderSenderOverride: e.target.value }))}
                    placeholder="91XXXXXXXXXX"
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleRunReminderNow}
                  disabled={runningReminderNow}
                  startIcon={runningReminderNow ? <CircularProgress size={16} /> : null}
                >
                  {runningReminderNow ? 'Running...' : 'Run Reminder Scan Now'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Staff Management (Admin Only) */}
        {hasAdminAccess && (
          <Grid size={{ xs: 12 }}>
            <Card sx={{ borderRadius: 3, width: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Staff Management
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={loadStaffManagementData}
                    disabled={staffLoading}
                  >
                    {staffLoading ? 'Refreshing...' : 'Refresh'}
                  </Button>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Approve or reject pending join requests and manage active staff roles.
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Share with staff: Business ID <strong>{user?.business?.id || 'N/A'}</strong>, Name <strong>{user?.business?.name || 'N/A'}</strong>
                </Alert>

                <Divider sx={{ mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Pending Requests ({pendingStaffRequests.length})
                </Typography>

                {pendingStaffRequests.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No pending requests.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                    {pendingStaffRequests.map((request) => (
                      <Box
                        key={request.id}
                        sx={{
                          border: '1px solid rgba(0,0,0,0.12)',
                          borderRadius: 2,
                          p: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 2,
                          flexWrap: 'wrap'
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {request.name || 'Unnamed User'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {request.email}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={staffActionLoadingId === `pending-${request.id}`}
                            onClick={() => handleApproveStaffRequest(request.id, 'staff')}
                          >
                            Approve Staff
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={staffActionLoadingId === `pending-${request.id}`}
                            onClick={() => handleApproveStaffRequest(request.id, 'admin')}
                          >
                            Approve Admin
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            disabled={staffActionLoadingId === `pending-${request.id}`}
                            onClick={() => handleRejectStaffRequest(request.id)}
                          >
                            Reject
                          </Button>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                <Divider sx={{ mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Active Staff ({staffMembers.length})
                </Typography>

                {staffMembers.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No active staff members yet.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {staffMembers.map((staff) => (
                      <Box
                        key={staff.id}
                        sx={{
                          border: '1px solid rgba(0,0,0,0.12)',
                          borderRadius: 2,
                          p: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 2,
                          flexWrap: 'wrap'
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {staff.name || 'Unnamed User'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {staff.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            Last active: {formatRelativeTime(staff.lastActivityAt)} ({formatDateTime(staff.lastActivityAt)})
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Last login: {formatDateTime(staff.lastLogin)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                          <Chip
                            label={String(staff.role || 'staff').toUpperCase()}
                            color={staff.role === 'admin' ? 'primary' : 'default'}
                            variant="outlined"
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={staffActionLoadingId === `active-${staff.id}`}
                            onClick={() => handleUpdateStaffRole(staff.id, staff.role === 'admin' ? 'staff' : 'admin')}
                          >
                            {staff.role === 'admin' ? 'Set Staff' : 'Make Admin'}
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            disabled={staffActionLoadingId === `active-${staff.id}`}
                            onClick={() => handleRemoveStaffAccess(staff.id, staff.name || 'this staff member')}
                          >
                            Remove Access
                          </Button>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Action Buttons */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2 }}>
          <Button
            variant="outlined"
            onClick={handleSyncData}
            disabled={syncing || saving}
            startIcon={syncing ? <CircularProgress size={16} /> : <CloudDownloadIcon />}
            sx={{
              borderRadius: 3,
              px: 4,
              py: 1.5,
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                color: 'primary.dark',
                background: 'rgba(102, 126, 234, 0.04)',
              }
            }}
          >
            {syncing ? 'Syncing...' : 'Sync Data'}
          </Button>

          <Button
            variant="contained"
            size="large"
            onClick={handleSave}
            disabled={saving || syncing}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            sx={{
              borderRadius: 3,
              px: 6,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
              }
            }}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ mt: 6, textAlign: 'center', py: 3, px: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
          Settings are automatically saved to your account and synced across devices.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', mt: 1 }}>
          For support or questions, contact us at support@dhandhapaani.store
        </Typography>
      </Box>
    </Box>
  );
}

export default Settings;

